import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFetcher } from 'react-router';
import { useLocalisation } from '@archipro-website/localisation';

import { useRootData } from '@modules/root';
import { Button } from '@rocco/components/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Input } from '@rocco/components/input';

import { SALE_SHARING_OPT_OUT_API_ROUTE } from '~/routes/remix-api.privacy.sale-sharing-opt-out';
import {
    SALE_SHARING_OPT_OUT_DEFAULT_VALUES,
    saleSharingOptOutSchema,
    type SaleSharingOptOutValues,
} from './sale-sharing-opt-out-schema';
import { getPrivacyChoicesCopy } from './privacy-choices-copy';

interface OptOutActionResult {
    ok: boolean;
    errors?: Record<string, string[]>;
}

/**
 * Self-service privacy opt-out for visitors who are not signed in, worded for
 * the market (see `getPrivacyChoicesCopy`). Mounted as a section of
 * `/your-privacy-choices`.
 *
 * Email is the only thing asked for, and nothing is verified: US state privacy
 * law requires this to be exercisable with no account and no identity
 * verification (CCR 7026(h), 7004), and every market gets the same form. The
 * email is not a credential — it is what extends the opt-out beyond this
 * browser to a matching account. The browser itself is opted out on submit
 * either way, via the cookie the endpoint sets.
 *
 * The confirmation deliberately does not say whether the address matched an
 * account, matching the endpoint, which would otherwise let anyone test whether
 * a given email has an ArchiPro account.
 */
export const SaleOrSharingOptOutForm = () => {
    const fetcher = useFetcher<OptOutActionResult>();
    const { country } = useLocalisation();
    const { stoppedActivity } = getPrivacyChoicesCopy(country);
    // Read from the document rather than the fetcher so the confirmation
    // survives the reload below, and a returning visitor sees their standing
    // choice rather than an empty form.
    //
    // `saleSharingOptOutPending` is what keeps a browser-only opt-out from
    // reading as a completed one: the endpoint sets the cookie even when the
    // backend write fails, so the cookie alone is not proof the record exists.
    const { saleSharingOptOut, saleSharingOptOutPending } = useRootData();
    const isSubmitting = fetcher.state !== 'idle';
    const isSuccess = fetcher.data?.ok === true;
    const hasFailed = fetcher.state === 'idle' && fetcher.data?.ok === false;
    const isRecorded =
        isSuccess || (saleSharingOptOut && !saleSharingOptOutPending);

    const form = useForm<SaleSharingOptOutValues>({
        resolver: zodResolver(saleSharingOptOutSchema),
        mode: 'onSubmit',
        defaultValues: SALE_SHARING_OPT_OUT_DEFAULT_VALUES,
    });

    /**
     * Reload once the opt-out is recorded, so the page is rebuilt from a
     * request that carries the cookie and the advertising tags never load.
     *
     * Re-rendering is not enough. The pixel components inject one-shot scripts
     * that install `fbq`, `pintrk` and friends on `window`; unmounting the
     * `<script>` tag leaves those globals in place and firing. Only a fresh
     * document tears them down, so a person who opts out stops being tracked
     * during the session they asked in, rather than at their next full page
     * load.
     */
    useEffect(() => {
        if (isSuccess) {
            window.location.reload();
        }
    }, [isSuccess]);

    const onSubmit = (values: SaleSharingOptOutValues) => {
        fetcher.submit(values, {
            method: 'post',
            action: SALE_SHARING_OPT_OUT_API_ROUTE,
        });
    };

    if (isRecorded) {
        return (
            <div
                className="space-y-2 rounded-md bg-gray-100 p-4"
                role="status"
                aria-live="polite"
            >
                <p className="font-medium text-gray-900">
                    Your opt-out has been recorded.
                </p>
                <p>
                    We have stopped {stoppedActivity} for this browser. Where
                    that email address matches an ArchiPro account, your choice
                    is recorded against it too. Repeat this on any other browser
                    or device you use, or sign in and manage the setting under
                    Settings, then Privacy.
                </p>
            </div>
        );
    }

    return (
        <Form {...form}>
            {saleSharingOptOutPending && !hasFailed && (
                <div
                    className="mb-3 max-w-md space-y-2 rounded-md bg-gray-100 p-4"
                    role="status"
                    aria-live="polite"
                >
                    <p className="font-medium text-gray-900">
                        This browser is opted out, but we could not save your
                        email.
                    </p>
                    <p>
                        Sharing has stopped for this browser. Submit again so we
                        can apply the same choice to any ArchiPro account using
                        your email address.
                    </p>
                </div>
            )}

            <fetcher.Form
                onSubmit={form.handleSubmit(onSubmit)}
                method="post"
                action={SALE_SHARING_OPT_OUT_API_ROUTE}
                // `type="email"` is kept for the mobile keyboard and autofill,
                // but native constraint validation would block submit before
                // the resolver runs and show a browser bubble instead of our
                // own message. zod is the single source of validation.
                noValidate
                className="max-w-md space-y-3"
            >
                <FormField
                    name="email"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Email address</FormLabel>
                            <FormControl>
                                <Input
                                    type="email"
                                    color="lightOutline"
                                    placeholder="you@example.com"
                                    autoComplete="email"
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <Button
                    type="submit"
                    color="dark"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                >
                    Submit my opt-out
                </Button>

                {hasFailed && (
                    <p role="alert" className="text-sm text-destructive">
                        We could not record your opt-out just now. Please try
                        again.
                    </p>
                )}
            </fetcher.Form>
        </Form>
    );
};
